import {
  Button,
  Grid,
  InputAdornment,
  styled,
  Typography
} from "@mui/material";
import { FormikProps } from "formik";
import { useRef, useState } from "react";
import {
  ContactForm,
  ContactFormValues,
  StyledTextField
} from "../../components/ContactForm";
import { GiftCardResults } from "../../components/GiftCard/GiftCardResults";
import { Layout } from "../../components/Layout";
import { extraordinaryColors } from "../../utils/theme";

export const StyledButton = styled(Button)<{ backgroundcolor: string }>(
  ({ backgroundcolor }) => ({
    color: "white",
    backgroundColor: backgroundcolor,
    width: 190,
    height: 55,
    fontSize: 30,
    borderRadius: 60,
    fontWeight: 700,
    "&:hover": {
      backgroundColor: backgroundcolor
    },
    fontFamily: "GT-Alpina-Condensed",
    textTransform: "none"
  })
);

export interface GiftCardResultsInterface {
  totalGiftCardValue: number;
  neverSpent: number;
  breakage: number;
  totalCardCost: number;
  totalBreakage: number;
  totalBreakagePercentage: number;
}

export const GiftCard = () => {
  const contactFormikRef = useRef<FormikProps<ContactFormValues>>();
  const [showResult, setShowResult] = useState(false);
  const [totalCardsYearly, setTotalCardsYearly] = useState<number | undefined>(
    undefined
  );
  const [averageValue, setAverageValue] = useState<number | undefined>(
    undefined
  );

  const [error, setError] = useState<string>();

  const neverSpentPercentage = 0.07;
  const breakagePercentage = 0.15;
  const cardCost = 6;

  const [results, setResults] = useState<GiftCardResultsInterface>({
    totalGiftCardValue: 0,
    neverSpent: 0,
    breakage: 0,
    totalCardCost: 0,
    totalBreakage: 0,
    totalBreakagePercentage: 0
  });

  const handleSubmit = () => {
    if (!contactFormikRef.current) return;
    contactFormikRef.current.validateForm();

    if (!totalCardsYearly) {
      setError("Total gift cards (Yearly) is required.");
      return;
    } else {
      setError("");
    }

    if (!averageValue) {
      setError("Average gift card value (per employee) is required.");
      return;
    } else {
      setError("");
    }

    contactFormikRef.current.handleSubmit();
  };

  const handleResults = () => {
    if (!totalCardsYearly || !averageValue) return;

    const totalGiftCardValue = totalCardsYearly * averageValue;
    const neverSpent = totalGiftCardValue * neverSpentPercentage;
    const breakage = totalGiftCardValue * breakagePercentage;
    const totalCardCost = totalCardsYearly * cardCost;

    const totalBreakage = neverSpent + breakage + totalCardCost;
    const totalBreakagePercentage = totalBreakage / totalGiftCardValue;

    setResults({
      totalGiftCardValue,
      neverSpent,
      breakage,
      totalCardCost,
      totalBreakage,
      totalBreakagePercentage
    });

    setShowResult(true);
  };

  return (
    <Layout>
      <Grid item xs={12}>
        <Typography variant="h2">Gift Card Breakage Calculator</Typography>
      </Grid>

      <Grid item xs={12} textAlign="left" mt={3}>
        {!showResult ? (
          <>
            <ContactForm
              ref={contactFormikRef}
              webflowSlug="gift-card-breakage-calculator"
              onSuccess={() => handleResults()}
              color={extraordinaryColors.pink.main}
              headingColor="black"
            />

            <Grid container mt={5}>
              <Grid item xs={12} mb={1}>
                <Typography fontWeight={700} fontSize="20px">
                  Complete the following{" "}
                </Typography>
                <Typography>
                  (The number of gift cards you distribute annually and the
                  average value of each)
                </Typography>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StyledTextField
                    textcolor={extraordinaryColors.pink.main}
                    type="number"
                    variant="standard"
                    value={totalCardsYearly}
                    onChange={(e) => setTotalCardsYearly(+e.target.value)}
                    name="totalCardsYearly"
                    placeholder="Total gift cards (Yearly)*"
                    fullWidth
                    size="small"
                    InputProps={{
                      inputProps: { min: 0, max: 100000 },
                      disableUnderline: true
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <StyledTextField
                    textcolor={extraordinaryColors.pink.main}
                    type="number"
                    variant="standard"
                    value={averageValue}
                    onChange={(e) => setAverageValue(+e.target.value)}
                    name="averageValue"
                    placeholder="Average gift card value (per employee)*"
                    fullWidth
                    size="small"
                    InputProps={{
                      disableUnderline: true,
                      inputProps: { min: 0, max: 100000 },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography
                            sx={{
                              color: extraordinaryColors.pink.main,
                              fontSize: 25,
                              fontWeight: 700,
                              fontFamily: "GT-Alpina-Condensed",
                              paddingBottom: 0.5
                            }}
                          >
                            $
                          </Typography>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {error && (
              <Typography my={2} color="error" fontWeight={600}>
                {error}
              </Typography>
            )}

            <Grid container justifyContent="flex-end" mt={3}>
              <Grid item>
                <StyledButton
                  backgroundcolor={extraordinaryColors.pink.main}
                  onClick={() => handleSubmit()}
                >
                  Calculate
                </StyledButton>
              </Grid>
            </Grid>
          </>
        ) : (
          <GiftCardResults
            neverSpentPercentage={neverSpentPercentage}
            breakagePercentage={breakagePercentage}
            cardCost={cardCost}
            results={results}
          />
        )}
      </Grid>
    </Layout>
  );
};
